import { isPlainObject } from "es-toolkit/compat";
import {
  BaseError,
  type CustomErrorClassOptionsType,
  type BaseErrorProps,
  type ErrorCtxType,
} from "./base.ts";

export interface HTTPRequestErrorProps {
  /** the API endpoint that was originally requested */
  endpoint?: string;
  /** the HTTP method used against the endpoint */
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS";
  /** the HTTP status code of the response */
  status?: number;
  /** the request content type */
  requestContentType?: string;
  /** the request query */
  requestQuery?: Record<
    string | number,
    string | number | string[] | number[] | undefined | boolean | null
  >;
  /** the request body, if JSON provided */
  requestBodyJson?: Record<string, unknown> | unknown[];
}

/**
 * Error class for errors that occur when an HTTP request is made.
 *
 * @param message - the error message
 * @param options - options for the error
 * @param options.endpoint - the API endpoint that was originally requested
 * @param options.method - the HTTP method used against the endpoint
 */
export const HTTPRequestError = BaseError.subclass("HTTPRequestError", {
  custom: class extends BaseError implements HTTPRequestErrorProps {
    /** @see {@link HTTPRequestErrorProps.endpoint} */
    readonly endpoint?: HTTPRequestErrorProps["endpoint"];
    /** @see {@link HTTPRequestErrorProps.method} */
    readonly method?: HTTPRequestErrorProps["method"];
    /** @see {@link HTTPRequestErrorProps.status} */
    readonly status?: HTTPRequestErrorProps["status"];
    /** @see {@link HTTPRequestErrorProps.requestQuery} */
    readonly requestQuery?: HTTPRequestErrorProps["requestQuery"];
    /** @see {@link HTTPRequestErrorProps.requestContentType} */
    readonly requestContentType?: HTTPRequestErrorProps["requestContentType"];
    /** @see {@link HTTPRequestErrorProps.requestBodyJson} */
    readonly requestBodyJson?: HTTPRequestErrorProps["requestBodyJson"];

    constructor(
      message: string,
      options: CustomErrorClassOptionsType<
        Partial<BaseErrorProps & HTTPRequestErrorProps>
      > = {}
    ) {
      super(message, {
        ...options,
        code:
          options.code ??
          options.status ??
          {
            GET: 1,
            POST: 2,
            PUT: 3,
            DELETE: 4,
            PATCH: 5,
            OPTIONS: 6,
          }[options.method ?? "GET"],
        getExplanation: options.getExplanation,
        defaultExplanation: "An unknown network request error occurred.",
      });

      this.endpoint = options.endpoint;
      this.method = options.method as HTTPRequestErrorProps["method"];
      this.requestContentType =
        options.requestContentType ?? "application/json";
      this.requestQuery = options.requestQuery;

      if (
        isPlainObject(options.requestBodyJson) ||
        Array.isArray(options.requestBodyJson)
      ) {
        this.requestBodyJson = options.requestBodyJson;
      }
    }
  },
});

export interface HTTPResponseErrorProps {
  /** the API endpoint that was originally requested */
  endpoint?: string;
  /** the HTTP method used against the endpoint */
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS";
  /** the HTTP status code of the response */
  status?: number;
  /** the request content type */
  requestContentType?: string | null;
  /** the request query */
  requestQuery?: Record<
    string | number,
    string | number | string[] | number[] | undefined | boolean | null
  >;
  /** the request body, if JSON provided */
  requestBodyJson?: Record<string, unknown> | unknown[];
  /** the response body, if JSON provided */
  responseBodyJson?: Record<string, unknown> | unknown[];
  /** the response content type */
  responseContentType?: string | null;
}

/**
 * Error class for errors that occur when an HTTP request fails and the server responds.
 *
 * @param message - the error message
 * @param options - options for the error
 * @param options.endpoint - the API endpoint that was originally requested
 * @param options.method - the HTTP method used against the endpoint
 */
export const HTTPResponseError = BaseError.subclass("HTTPResponseError", {
  custom: class extends BaseError implements HTTPResponseErrorProps {
    /** @see {@link HTTPResponseErrorProps.endpoint} */
    readonly endpoint?: HTTPResponseErrorProps["endpoint"];
    /** @see {@link HTTPResponseErrorProps.method} */
    readonly method?: HTTPResponseErrorProps["method"];
    /** @see {@link HTTPResponseErrorProps.status} */
    readonly status?: HTTPResponseErrorProps["status"];
    /** @see {@link HTTPResponseErrorProps.requestContentType} */
    readonly requestContentType?: HTTPResponseErrorProps["requestContentType"];
    /** @see {@link HTTPResponseErrorProps.requestQuery} */
    readonly requestQuery?: HTTPResponseErrorProps["requestQuery"];
    /** @see {@link HTTPResponseErrorProps.requestBodyJson} */
    readonly requestBodyJson?: HTTPResponseErrorProps["requestBodyJson"];
    /** @see {@link HTTPResponseErrorProps.responseContentType} */
    readonly responseContentType?: HTTPResponseErrorProps["responseContentType"];
    /** @see {@link HTTPResponseErrorProps.responseBodyJson} */
    readonly responseBodyJson?: HTTPResponseErrorProps["responseBodyJson"];

    constructor(
      message: string,
      options: CustomErrorClassOptionsType<
        Partial<BaseErrorProps & HTTPResponseErrorProps>
      > = {}
    ) {
      super(message, {
        ...options,
        code: options.code ?? options.status ?? 500,
        getExplanation: options.getExplanation,
        defaultExplanation: "An unknown network response error occurred.",
      });

      this.endpoint = options.endpoint;
      this.method = options.method as HTTPResponseErrorProps["method"];
      this.requestContentType =
        options.requestContentType ?? "application/json";
      this.requestQuery = options.requestQuery;
      this.requestBodyJson = options.requestBodyJson;
      this.status = options.status;
      this.responseContentType =
        options.responseContentType ?? "application/json";

      if (
        isPlainObject(options.responseBodyJson) ||
        Array.isArray(options.responseBodyJson)
      ) {
        this.responseBodyJson = options.responseBodyJson;
      }
    }
  },
});

/**
 * Error class for errors that occur when the server encounters an error while processing a request.
 *
 * @param message - the error message
 * @param options - options for the error
 */
export const HTTPServerError = HTTPResponseError.subclass("HTTPServerError", {
  custom: class extends HTTPResponseError {
    constructor(
      message: string,
      options: CustomErrorClassOptionsType<Partial<HTTPResponseErrorProps>> = {}
    ) {
      super(message, {
        ...options,
        // following properties cannot be overridden and is intentional
        code: 500,
      });

      this._getExplanation = () =>
        "The server encountered an error while processing your request.";
    }
  },
});

/**
 * Error class for errors that occur when the initiator is not authorized to access a resource.
 *
 * @param message - the error message
 * @param options - options for the error
 */
export const HTTPUnauthorizedError = HTTPResponseError.subclass(
  "HTTPUnauthorizedError",
  {
    custom: class extends HTTPResponseError {
      constructor(
        message: string,
        options: CustomErrorClassOptionsType<
          Partial<HTTPResponseErrorProps>
        > = {}
      ) {
        super(message, {
          ...options,
          // following properties cannot be overridden and is intentional
          code: 401,
          severity: "warning",
        });

        this._getExplanation = () =>
          "You are not authorized to access this resource.";
      }
    },
  }
);

export interface HTTPNotFoundErrorCtx extends ErrorCtxType {
  resource?: string;
  resourceId?: string;
}
/**
 * Error class for errors that occur when a resource is not found.
 *
 * @param message - the error message
 * @param options - options for the error
 * @param options.resource - the resource that was not found
 * @param options.resourceId - the ID of the resource that was not found
 */
export const HTTPNotFoundError = HTTPResponseError.subclass(
  "HTTPNotFoundError",
  {
    custom: class extends HTTPResponseError {
      constructor(
        message: string,
        options: CustomErrorClassOptionsType<
          Partial<HTTPResponseErrorProps>
        > = {}
      ) {
        super(message, {
          ...options,
          // following properties cannot be overridden and is intentional
          code: 404,
          severity: "warning",
        });

        this._getExplanation = (ctx: HTTPNotFoundErrorCtx = {}) =>
          ctx.resource && ctx.resourceId
            ? `The resource "${ctx.resource}" with id ${ctx.resourceId} was not found.`
            : "The resource was not found.";
      }
    },
  }
);

export interface HTTPForbiddenErrorCtx extends ErrorCtxType {
  target?: string;
}
/**
 * Error class for errors that occur when a user does not have the necessary
 * permissions to complete an HTTP request.
 *
 * @param message - the error message
 * @param options - options for the error
 * @param options.target - the target of the action (e.g. "delete report")
 */
export const HTTPForbiddenError = HTTPResponseError.subclass(
  "HTTPForbiddenError",
  {
    custom: class extends HTTPResponseError {
      constructor(
        message: string,
        options: CustomErrorClassOptionsType<
          Partial<HTTPResponseErrorProps>
        > = {}
      ) {
        super(message, {
          ...options,
          // following properties cannot be overridden and is intentional
          code: 403,
          severity: "warning",
        });

        this._getExplanation = (ctx: HTTPForbiddenErrorCtx = {}) =>
          ctx.target
            ? `You do not have the necessary permissions to complete the "${ctx.target}" action.`
            : "You do not have the necessary permissions to complete the action.";
      }
    },
  }
);
